import * as React from 'react';
import {FunctionComponent, useState} from 'react';
import {classNames} from '../../../../Support/classNames';
import {GraphableSnippet} from '../Components/Models/GraphableSnippet';
import {ReportData} from '../CompareReportPresenter';
import {strokesForSnippet} from '../Utils/strokesForSnippet';
import {formatSplit} from '../../../../Support/Formatting/formatSplit';
import {Speed} from '../../../../../Domain/Speed';
import {Duration} from '../../../../../Domain/Duration';
import {BaseGraph} from '../Components/Components/BaseGraph';
import {formatPower} from '../../../../Support/Formatting/formatPower';
import {Power} from '../../../../../Domain/Power';
import {StrokeRate} from '../../../../../Domain/StrokeRate';
import {formatStrokeRate} from '../../../../Support/Formatting/formatStrokeRate';
import {Distance} from '../../../../../Domain/Distance';
import {formatStrokeLength} from '../../../../Support/Formatting/formatStrokeLength';
import {Fraction} from '../../../../../Domain/Fraction';
import {formatRelativePeakForcePosition} from '../../../../Support/Formatting/formatRelativePeakForcePosition';
import Dropdown from 'react-bootstrap/Dropdown';
import {Stroke} from '../../../../../Models/Stroke';
import {DualRange} from '../../../../Components/Form/DualRange';
import {selectRange} from './Helper/SelectRange';
import {InfoTooltip} from '../../../../Components/Utils/InfoTooltip';

interface OwnProps {
    reportData: ReportData;
    className?: string;
}

enum GraphType {
    Split = 'split',
    Power = 'power',
    StrokeRate = 'stroke-rate',
    StrokeLength = 'stroke-length',
    RelativePeakForcePosition = 'relative-peak-force-position',
}

function toTools(graph: GraphType): {
    label: string;
    formatY: (value: number) => string;
    selectYValue: (stroke: Stroke) => number;
} {
    switch (graph) {
        case GraphType.Split:
            return {
                label: 'Split (/500m)',
                formatY: (secondsPer500Meters) =>
                    formatSplit(Speed.fromDurationPer500Meters(Duration.fromSeconds(secondsPer500Meters))).formatted,
                selectYValue: (stroke) => stroke.speed?.durationPer500Meters?.seconds || 0,
            };
        case GraphType.Power:
            return {
                label: 'Power (W)',
                formatY: (watts) => formatPower(Power.fromWatts(watts)).formatted,
                selectYValue: (stroke) => stroke.power?.watts || 0,
            };
        case GraphType.StrokeRate:
            return {
                label: 'Stroke Rate (/min)',
                formatY: (strokeRate) => formatStrokeRate(StrokeRate.fromStrokesPerMinute(strokeRate)).formatted,
                selectYValue: (stroke) => stroke.strokeRate?.strokesPerMinute || 0,
            };
        case GraphType.StrokeLength:
            return {
                label: 'Stroke Length (M)',
                formatY: (strokeLength) => formatStrokeLength(Distance.fromMeters(strokeLength)).formatted,
                selectYValue: (stroke) => stroke.strokeLength?.meters || 0,
            };
        case GraphType.RelativePeakForcePosition:
            return {
                label: 'Relative Peak Force Position (%)',
                formatY: (value) => formatRelativePeakForcePosition(Fraction.fromValue(value)).formatted,
                selectYValue: (stroke) => stroke.relativePeakForcePosition?.value || 0,
            };
    }
}

export const Graphs: FunctionComponent<OwnProps> = ({reportData, className}) => {
    const [visibleRange, setVisibleRange] = useState<{min: number | null; max: number | null}>({
        min: null,
        max: null,
    });
    const [visibleGraphs, setVisibleGraphs] = useState<GraphType[]>([
        GraphType.Split,
        GraphType.Power,
        GraphType.StrokeRate,
    ]);
    const [normaliseXAxis, setNormaliseXAxis] = useState(false);
    const snippets = [...reportData.snippets].sort((a, b) => {
        return a.publicWorkout.executedAt.getTime() - b.publicWorkout.executedAt.getTime();
    });

    const graphableSnippets: GraphableSnippet[] = snippets
        .map((snippet) => {
            const downloadedIndoorWorkout = reportData.downloadedIndoorWorkouts.find((dIW) => {
                return dIW.id === snippet.publicWorkout.id;
            });
            return {
                snippet: snippet,
                strokes: downloadedIndoorWorkout ? strokesForSnippet(snippet, downloadedIndoorWorkout) : null,
            };
        })
        .filter((snippet): snippet is GraphableSnippet => snippet.strokes !== null);
    const maxXValue = Math.max(
        ...graphableSnippets.flatMap((graphableSnippet) => {
            return (
                graphableSnippet.strokes[graphableSnippet.strokes.length - 1].totalTime.milliseconds -
                graphableSnippet.strokes[0].totalTime.milliseconds
            );
        }),
    );

    return (
        <div className={classNames('card', className)}>
            <div className="card-title d-flex flex-row justify-content-between">
                <h5>Graphs</h5>
                <div className="d-flex flex-row justify-content-end gap-1 align-content-center me-2">
                    <div className="form-check mt-2 form-switch">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="normaliseXAxis"
                            checked={normaliseXAxis}
                            onChange={() => {
                                setNormaliseXAxis(!normaliseXAxis);
                                setVisibleRange({min: null, max: null});
                            }}
                        />
                        <label
                            className="form-check-label d-flex flex-row justify-content-start"
                            htmlFor="normaliseXAxis"
                        >
                            Align x-axes
                            <InfoTooltip
                                className="ms-2"
                                description="Align x-axes to start at 0 and end at 100% of the duration of the workout"
                            />
                        </label>
                    </div>
                    <Dropdown className="dropdown p-2" autoClose="outside">
                        <Dropdown.Toggle
                            className="btn btn-secondary btn-sm dropdown-toggle"
                            type="button"
                            id="dropdown-autoclose-outside"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                        >
                            Graphs
                        </Dropdown.Toggle>
                        <Dropdown.Menu aria-labelledby="visibilityDropdown">
                            {Object.values(GraphType).map((item) => (
                                <li key={item}>
                                    <label className="dropdown-item">
                                        <input
                                            type="checkbox"
                                            checked={visibleGraphs.includes(item as GraphType)}
                                            onChange={() => {
                                                if (visibleGraphs.includes(item as GraphType)) {
                                                    setVisibleGraphs(visibleGraphs.filter((graph) => graph !== item));
                                                } else {
                                                    setVisibleGraphs([...visibleGraphs, item as GraphType]);
                                                }
                                            }}
                                        />
                                        <span className="ms-1">{toTools(item as GraphType).label}</span>
                                    </label>
                                </li>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </div>

            <div className="card-body graphs">
                {visibleGraphs.map((visibleGraph) => {
                    const tools = toTools(visibleGraph);

                    return (
                        <div className="row" key={visibleGraph}>
                            <div className="col-12">
                                <p className="graphs__label">{tools.label}</p>
                                <BaseGraph
                                    formatY={tools.formatY}
                                    selectYValue={tools.selectYValue}
                                    normaliseXAxis={normaliseXAxis}
                                    graphableSnippets={graphableSnippets
                                        .map((graphableSnippet) => {
                                            return {
                                                ...graphableSnippet,
                                                strokes: selectRange(
                                                    graphableSnippet.strokes,
                                                    visibleRange,
                                                    normaliseXAxis,
                                                ),
                                            };
                                        })
                                        .filter((graphableSnippet) => graphableSnippet.strokes.length > 2)}
                                />
                            </div>
                        </div>
                    );
                })}
                {visibleGraphs.length > 0 && (
                    <div className="row">
                        <div className="col-12">
                            {normaliseXAxis && (
                                <DualRange
                                    value={{
                                        min: visibleRange.min || 0,
                                        max: visibleRange.max || 100,
                                    }}
                                    onChange={(r) => {
                                        setVisibleRange(r);
                                    }}
                                    min={0}
                                    max={100}
                                    step={1}
                                />
                            )}
                            {!normaliseXAxis && (
                                <DualRange
                                    value={{
                                        min: visibleRange.min || 0,
                                        max: Math.ceil((visibleRange.max || maxXValue) / 1000) * 1000,
                                    }}
                                    onChange={(r) => {
                                        setVisibleRange(r);
                                    }}
                                    min={0}
                                    max={Math.ceil(maxXValue / 1000) * 1000}
                                    step={1000}
                                />
                            )}
                        </div>
                    </div>
                )}
                {visibleGraphs.length === 0 && (
                    <div className="row">
                        <div className="col-12">
                            <p>No graph is selected. Please select a graph above by clicking the "Graphs" button.</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
